import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, PackageCheck, Mail, MapPinned } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const money = (n) => (n === null || n === undefined ? "—" : "$" + Number(n).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

export const Bookings = () => {
  const [orders, setOrders] = useState(null);
  const [consignments, setConsignments] = useState([]);
  const [locations, setLocations] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/machship/bookable-orders").then(({ data }) => setOrders(data.orders || [])).catch((e) => { setOrders([]); toast.error(formatApiError(e, "Could not load orders")); });
    api.get("/machship/consignments").then(({ data }) => setConsignments(data.consignments || [])).catch(() => {});
    api.get("/machship/locations").then(({ data }) => setLocations(data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const openForm = (order) => setForm({
    cart_id: order.cart_id,
    reference: order.reference,
    to_name: order.shipping?.name || "",
    to_contact: order.shipping?.name || "",
    to_phone: order.shipping?.phone || "",
    to_email: order.email || "",
    to_address_line1: order.shipping?.line1 || "",
    to_address_line2: order.shipping?.line2 || "",
    to_suburb: order.shipping?.suburb || order.fulfilment?.suburb || "",
    to_postcode: order.shipping?.postcode || order.fulfilment?.postcode || "",
    special_instructions: "",
    residential: false,
    tailgate: false,
  });

  const syncLocations = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/machship/locations/sync", {});
      const made = (data.locations || []).filter((l) => l.created).length;
      toast.success(made ? `${made} warehouse address saved in MachShip` : "Warehouse addresses already saved in MachShip");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not save the warehouse addresses"));
    } finally {
      setBusy(false);
    }
  };

  const book = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/machship/book", {
        cart_id: form.cart_id,
        to_name: form.to_name.trim(),
        to_contact: form.to_contact.trim(),
        to_phone: form.to_phone.trim(),
        to_email: form.to_email.trim(),
        to_address_line1: form.to_address_line1.trim(),
        to_address_line2: form.to_address_line2.trim(),
        to_suburb: form.to_suburb.trim().toUpperCase(),
        to_postcode: form.to_postcode.trim(),
        special_instructions: form.special_instructions.trim(),
        residential: form.residential,
        tailgate: form.tailgate,
      });
      toast.success(`Booked ${data.consignment.consignment_number} with ${data.consignment.carrier}`);
      if (data.label_email?.status !== "sent") toast.error(`Labels were not emailed: ${data.label_email?.error || "email not sent"}`);
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Gem did not accept this booking"));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "rounded-sm border border-ei-mist px-3 py-2 text-sm";

  return (
    <div className="mt-6 grid gap-5" data-testid="machship-bookings">
      {locations && (
        <div className="bg-white border border-ei-mist rounded-md p-4 text-sm" data-testid="machship-locations">
          <p className="flex items-center gap-2 font-semibold text-ei-navy"><MapPinned className="w-4 h-4" /> Warehouse addresses in MachShip</p>
          <ul className="mt-2 grid gap-1 text-xs text-ei-graphite/80">
            {locations.warehouses.map((w) => (
              <li key={w.id} data-testid={`machship-location-${w.id}`}>
                <strong>{w.name}</strong> — {w.address} · {w.saved?.company_location_id ? `saved (${w.saved.abbreviation})` : "not saved yet, quotes use suburb and postcode"}
              </li>
            ))}
          </ul>
          <button type="button" onClick={syncLocations} disabled={busy} className="mt-3 inline-flex items-center gap-2 font-display text-sm font-bold text-ei-navy border border-ei-navy/25 px-4 py-2.5 rounded-sm hover:border-ei-navy min-h-[44px] disabled:opacity-60" data-testid="machship-sync-locations">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPinned className="w-4 h-4" />} Save warehouse addresses to MachShip
          </button>
          <p className="mt-2 flex items-center gap-2 text-xs text-ei-graphite/60"><Mail className="w-3.5 h-3.5" /> Labels and the connote are emailed to {locations.label_email}</p>
        </div>
      )}

      <div className="bg-white border border-ei-mist rounded-md p-4" data-testid="machship-orders">
        <p className="font-semibold text-ei-navy text-sm">Paid delivery orders</p>
        {orders === null ? (
          <p className="mt-3 flex items-center gap-2 text-sm text-ei-graphite/70"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
        ) : orders.length === 0 ? (
          <p className="mt-2 text-sm text-ei-graphite/70" data-testid="machship-orders-empty">No paid delivery orders are waiting to be booked.</p>
        ) : (
          <ul className="mt-3 grid gap-2">
            {orders.map((order) => (
              <li key={order.cart_id} className="flex flex-wrap items-center justify-between gap-3 border border-ei-mist/70 rounded-sm px-3 py-2.5" data-testid={`machship-order-${order.cart_id}`}>
                <div className="text-sm">
                  <p className="font-semibold text-ei-navy">{order.reference || order.cart_id}</p>
                  <p className="text-xs text-ei-graphite/60">{order.lines.map((l) => `${l.name || l.id} ×${l.quantity}`).join(", ")}</p>
                  <p className="text-xs text-ei-graphite/60">From {order.dispatch_warehouse || "—"} · freight charged {money(order.freight_charged_inc_gst)}</p>
                </div>
                {order.booked ? (
                  <span className="text-xs font-semibold text-emerald-800 bg-emerald-50 px-2.5 py-1 rounded-full">Booked</span>
                ) : (
                  <button type="button" onClick={() => openForm(order)} className="inline-flex items-center gap-2 font-display text-sm font-bold bg-ei-navy text-ei-white px-4 py-2.5 rounded-sm hover:bg-ei-ink min-h-[44px]" data-testid={`machship-book-${order.cart_id}`}>
                    <PackageCheck className="w-4 h-4" /> Book with Gem
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {form && (
        <form onSubmit={book} className="bg-white border border-ei-navy/30 rounded-md p-5 grid gap-3" data-testid="machship-booking-form">
          <p className="font-semibold text-ei-navy text-sm">Book {form.reference || form.cart_id} — this creates a real consignment on our Gem account</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="grid gap-1 text-xs font-semibold text-ei-graphite/70">Deliver to name
              <input required value={form.to_name} onChange={(e) => setForm((f) => ({ ...f, to_name: e.target.value }))} className={inputCls} data-testid="booking-name" />
            </label>
            <label className="grid gap-1 text-xs font-semibold text-ei-graphite/70">Site contact phone
              <input value={form.to_phone} onChange={(e) => setForm((f) => ({ ...f, to_phone: e.target.value }))} className={inputCls} data-testid="booking-phone" />
            </label>
            <label className="grid gap-1 text-xs font-semibold text-ei-graphite/70">Address line 1
              <input required value={form.to_address_line1} onChange={(e) => setForm((f) => ({ ...f, to_address_line1: e.target.value }))} className={inputCls} data-testid="booking-line1" />
            </label>
            <label className="grid gap-1 text-xs font-semibold text-ei-graphite/70">Address line 2
              <input value={form.to_address_line2} onChange={(e) => setForm((f) => ({ ...f, to_address_line2: e.target.value }))} className={inputCls} data-testid="booking-line2" />
            </label>
            <label className="grid gap-1 text-xs font-semibold text-ei-graphite/70">Suburb
              <input required value={form.to_suburb} onChange={(e) => setForm((f) => ({ ...f, to_suburb: e.target.value }))} className={inputCls} data-testid="booking-suburb" />
            </label>
            <label className="grid gap-1 text-xs font-semibold text-ei-graphite/70">Postcode
              <input required pattern="\d{4}" value={form.to_postcode} onChange={(e) => setForm((f) => ({ ...f, to_postcode: e.target.value }))} className={`${inputCls} tnum`} data-testid="booking-postcode" />
            </label>
          </div>
          <input value={form.special_instructions} onChange={(e) => setForm((f) => ({ ...f, special_instructions: e.target.value }))} placeholder="Special instructions for the driver" className={inputCls} data-testid="booking-instructions" />
          <div className="flex flex-wrap gap-4 text-sm text-ei-graphite/80">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.residential} onChange={(e) => setForm((f) => ({ ...f, residential: e.target.checked }))} data-testid="booking-residential" /> Residential delivery</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.tailgate} onChange={(e) => setForm((f) => ({ ...f, tailgate: e.target.checked }))} data-testid="booking-tailgate" /> Tailgate required</label>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="submit" disabled={busy} className="inline-flex items-center gap-2 font-display text-sm font-bold bg-ei-navy text-ei-white px-4 py-2.5 rounded-sm hover:bg-ei-ink min-h-[44px] disabled:opacity-60" data-testid="booking-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageCheck className="w-4 h-4" />} Book and email the labels
            </button>
            <button type="button" onClick={() => setForm(null)} className="font-display text-sm font-bold text-ei-navy px-4 py-2.5 rounded-sm hover:bg-ei-mist/40 min-h-[44px]" data-testid="booking-cancel">Cancel</button>
          </div>
        </form>
      )}

      {consignments.length > 0 && (
        <div className="bg-white border border-ei-mist rounded-md overflow-x-auto" data-testid="machship-consignment-list">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ei-graphite/60 border-b border-ei-mist">
                <th className="px-4 py-3">Consignment</th>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Carrier</th>
                <th className="px-4 py-3">To</th>
                <th className="px-4 py-3">Tracking</th>
                <th className="px-4 py-3">Emails</th>
              </tr>
            </thead>
            <tbody>
              {consignments.map((c) => (
                <tr key={c.consignment_id} className="border-b border-ei-mist/60 last:border-0" data-testid={`machship-consignment-${c.consignment_id}`}>
                  <td className="px-4 py-3 font-semibold text-ei-navy">{c.consignment_number}<span className="block text-xs font-normal text-ei-graphite/60">{c.status}</span></td>
                  <td className="px-4 py-3 text-xs text-ei-graphite/70">{c.reference}</td>
                  <td className="px-4 py-3 text-xs text-ei-graphite/70">{c.carrier}<span className="block">{c.service}</span></td>
                  <td className="px-4 py-3 text-xs text-ei-graphite/70">{c.delivery_suburb} {c.delivery_postcode}</td>
                  <td className="px-4 py-3 text-xs">
                    {c.tracking_url
                      ? <a href={c.tracking_url} target="_blank" rel="noreferrer" className="font-semibold text-ei-navy underline" data-testid={`machship-tracking-${c.consignment_id}`}>Track</a>
                      : <span className="text-ei-graphite/50">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-ei-graphite/70" data-testid={`machship-emails-${c.consignment_id}`}>
                    Labels: {c.label_email?.status === "sent" ? `sent to ${c.label_email.to}` : c.label_email?.error || "not sent"}
                    <span className="block">Customer: {c.customer_email?.status === "sent" ? `sent to ${c.customer_email.to}` : c.customer_email?.status === "no_address" ? "no email on order" : c.customer_email?.error || "not sent"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
